import { useEffect, useMemo, useRef, useState } from 'react'
import { FilterX, Search } from 'lucide-react'

import { useDashboardFilters, type FieldFilter } from '@/lib/dashboards'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/ui/button-variants'
import { Input } from '@/ui/input'

import type { DataGridColumnSchema, DataGridFacet, DataGridFilter } from '../lib/types'
import { FilterSourceProvider, type FilterFacetState, type FilterSource } from './data-grid-filter-source'
import { DataGridFilters } from './data-grid-filters'

/**
 * Loads the values a category field can take under the dashboard's other filters, with counts.
 * Without it, a category offers the options its field declares.
 */
export type DashboardOptionLoader = (
  field: string,
  search: string,
  filters: readonly FieldFilter[],
  signal: AbortSignal,
  include: readonly string[],
) => Promise<DataGridFacet | undefined>

/** The dashboard's search and filters, edited with the same controls a data grid uses. */
export function DashboardFilterBar({ className, loadOptions }: { className?: string; loadOptions?: DashboardOptionLoader }) {
  const dashboard = useDashboardFilters()
  const { contract } = dashboard
  const [facetState, setFacetState] = useState<Partial<Record<string, FilterFacetState>>>({})
  const aborts = useRef<Record<string, AbortController>>({})

  const columns = useMemo(() => Object.entries(contract.fields).map(([id, field]): DataGridColumnSchema => ({
    id,
    label: field.label,
    kind: field.kind,
    nullable: true,
    searchable: false,
    sortable: false,
    filterable: true,
    hiddenByDefault: false,
    alwaysSelected: false,
    operators: field.operators,
    labels: field.options,
    field: id,
  })), [contract])

  const facets = useMemo(() => Object.fromEntries(Object.entries(contract.fields)
    .filter(([, field]) => field.options)
    .map(([id, field]): [string, DataGridFacet] => [id, {
      column: id,
      values: Object.entries(field.options!).map(([value, label]) => ({ value, label })),
      hasMore: false,
    }])), [contract])

  function selectedValues(field: string): string[] {
    const filter = dashboard.filters.find((item) => item.field === field)
    return filter?.operator === 'in' && Array.isArray(filter.values) ? filter.values as string[] : []
  }

  const source: FilterSource = {
    columns,
    filters: dashboard.filters.map((filter) => ({ column: filter.field, operator: filter.operator, values: filter.values as DataGridFilter['values'] })),
    setFilters: (next) => dashboard.replace(next.map((filter) => ({ field: filter.column, operator: filter.operator, values: filter.values }))),
    facets,
    facetState,
    loadFacetOptions: async (field, search = '') => {
      if (!loadOptions) return
      aborts.current[field]?.abort()
      const controller = new AbortController()
      aborts.current[field] = controller
      setFacetState((state) => ({ ...state, [field]: { search, isLoading: true, error: null, facet: state[field]?.facet } }))
      try {
        const others = dashboard.filters.filter((filter) => filter.field !== field)
        const facet = await loadOptions(field, search, others, controller.signal, selectedValues(field))
        setFacetState((state) => ({ ...state, [field]: { search, isLoading: false, error: null, facet } }))
      } catch (error) {
        if (controller.signal.aborted) return
        setFacetState((state) => ({ ...state, [field]: { search, isLoading: false, error: error as Error, facet: state[field]?.facet } }))
      }
    },
  }

  // A category without fixed options, such as an ID, needs its selected values looked up to show their labels.
  const labelKey = JSON.stringify(dashboard.filters.filter((filter) =>
    contract.fields[filter.field]?.kind === 'category' && !contract.fields[filter.field]?.options))
  const loadLabels = useRef<(field: string) => void>(() => {})
  // Declared first so the lookup below always calls the current loader.
  useEffect(() => {
    loadLabels.current = (field) => void source.loadFacetOptions(field)
  })
  useEffect(() => {
    for (const filter of JSON.parse(labelKey) as FieldFilter[]) loadLabels.current(filter.field)
  }, [labelKey])

  return (
    <div role="group" aria-label="Dashboard filters" className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {contract.searchMinimumLength !== null && (
        <div className="relative">
          <Search aria-hidden="true" className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2" />
          <Input
            aria-label="Search"
            value={dashboard.searchInput}
            onChange={(event) => dashboard.setSearch(event.target.value)}
            placeholder="Search"
            maxLength={200}
            className="h-7 w-48 pl-7 text-xs"
          />
        </div>
      )}
      <FilterSourceProvider source={source}>
        <DataGridFilters />
      </FilterSourceProvider>
      {(dashboard.filters.length > 0 || dashboard.searchInput) && (
        <button
          type="button"
          onClick={dashboard.clear}
          aria-label="Clear all filters"
          title="Clear all filters"
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'text-muted-foreground')}
        >
          <FilterX aria-hidden="true" className="size-3.5" />
        </button>
      )}
    </div>
  )
}
